import type { JWTPayload } from 'jose';
import { decodeJwt, jwtVerify } from 'jose';

import type { Token } from './types';

/**
 * Secure JWT token verification and decoding utility.
 *
 * For security-critical operations, tokens should be verified using the public key.
 * If no public key is available, decoding is used with proper error handling,
 * but the backend MUST validate all security-critical operations.
 */

/**
 * Get JWT public key for verification (RS256).
 * This should be fetched from your backend's JWKS endpoint or configured via environment variable.
 *
 * Example JWKS endpoint: `${API_BASE_URL}/.well-known/jwks.json`
 *
 * @returns Public key as CryptoKey or null if not available
 */
// eslint-disable-next-line @typescript-eslint/require-await
async function getPublicKey(): Promise<CryptoKey | null> {
  // TODO: Implement fetching public key from backend JWKS endpoint
  // Example:
  // const jwksUrl = `${import.meta.env.VITE_API_BASE_URL}/.well-known/jwks.json`;
  // const jwks = await fetch(jwksUrl).then(res => res.json());
  // return await importJWK(jwks.keys[0]);

  // For now, return null to use safe decoding with error handling
  return null;
}

/**
 * Safely decode a JWT token without verification.
 * Only use this for non-security-critical operations like checking expiration for UI optimization.
 *
 * @param token - JWT token string
 * @returns Decoded token payload or null if decoding fails
 */
function safeDecodeToken<T extends JWTPayload = Token>(token: string): T | null {
  try {
    return decodeJwt<T>(token);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Verify and decode a JWT token using cryptographic verification.
 * This is the secure method that validates the token signature.
 *
 * @param token - JWT token string
 * @param publicKey - Public key for verification (optional, will attempt to fetch if not provided)
 * @returns Verified and decoded token payload or null if verification fails
 */
async function verifyToken<T extends JWTPayload = Token>(
  token: string,
  publicKey?: CryptoKey | null,
): Promise<T | null> {
  try {
    const key = publicKey || (await getPublicKey());

    if (!key) {
      // If no public key is available, fall back to safe decoding
      // This should only happen in development or if backend doesn't expose public key
      console.warn(
        'JWT public key not available. Using safe decode. Backend must validate all security-critical operations.',
      );
      return safeDecodeToken<T>(token);
    }

    // Verify token signature and decode
    const { payload } = await jwtVerify(token, key, {
      algorithms: ['RS256'], // Explicitly restrict to RS256 for security
    });

    return payload as T;
  } catch (error) {
    console.error('JWT verification failed:', error);
    return null;
  }
}

/**
 * Extract expiration time from token safely.
 * Used for UI optimization (e.g., refreshing token before expiry).
 * Backend validation is still required for security.
 *
 * @param token - JWT token string
 * @returns Expiration timestamp in seconds, or null if token is invalid
 */
export function getTokenExpiration(token: string | undefined | null): number | null {
  if (!token) {
    return null;
  }

  const decoded = safeDecodeToken<Token>(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  return Number(decoded.exp);
}

/**
 * Check if token is expired or will expire soon.
 * Used for UI optimization only. Backend validation is still required.
 *
 * @param token - JWT token string
 * @param bufferSeconds - Buffer time in seconds before considering token expired (default: 5)
 * @returns True if token is expired or will expire within buffer time
 */
export function isTokenExpired(token: string | undefined | null, bufferSeconds = 5): boolean {
  const exp = getTokenExpiration(token);
  if (exp === null) {
    return true; // Consider invalid tokens as expired
  }

  const now = Math.floor(Date.now() / 1000);
  return now >= exp - bufferSeconds;
}

/**
 * Safely decode token to extract claims.
 * For security-critical operations, use verifyTokenSecure() instead.
 *
 * @param token - JWT token string
 * @returns Decoded token payload or null if decoding fails
 */
export function decodeToken<T extends JWTPayload = Token>(
  token: string | undefined | null,
): T | null {
  if (!token) {
    return null;
  }

  return safeDecodeToken<T>(token);
}

/**
 * Verify token with cryptographic signature validation.
 * Use this for security-critical operations.
 *
 * @param token - JWT token string
 * @returns Verified token payload or null if verification fails
 */
export async function verifyTokenSecure<T extends JWTPayload = Token>(
  token: string | undefined | null,
): Promise<T | null> {
  if (!token) {
    return null;
  }

  return verifyToken<T>(token);
}
